import colorPallete from './colorPallete';
import buildBrandShades from './brandShades';

export default function buildColors(themeColor) {
  const brand = buildBrandShades(themeColor);

  const primary = {
    lightest: brand[50],
    veryLight: brand[100],
    veryLightAlt: brand[150],
    light: brand[200],
    soft: brand[500],
    main: brand.main,
    dark: brand[800],
    darker: brand[900],
    text: brand.main,
    textContrast: brand.contrastText,
    contrastText: brand.contrastText,
  };

  return {
    background: {
      lightGray: colorPallete.lambda600,
      lightblack: colorPallete.kappa400,
      sideBar: colorPallete.lambda100,
      header: colorPallete.grayWhiteGradient,
    },

    text: {
      primary: colorPallete.kappa700,
      secondary: colorPallete.lota700,
      accent: primary.main,
      sideBar: colorPallete.lambda800,
      table: colorPallete.gamma200,
      pagination: colorPallete.lota900,
      replenText: colorPallete.gamma800,
    },

    primary,

    primaryButton: {
      main: primary.main,
      dark: primary.darker,
      text: primary.text,
      textContrast: primary.textContrast,
      contrastText: primary.contrastText,
    },

    colorShades: {
      // red
      red: colorPallete.alpha900,
      veryLightRed: colorPallete.alpha100,
      mediumRed: colorPallete.alpha600,
      lightRed: colorPallete.alpha700,
      darkgrayishred: colorPallete.lambda700,
      // white
      white: colorPallete.lota700,
      mediumWhite: colorPallete.lota100,
      lightWhite: colorPallete.lota500,
      pureWhite: colorPallete.lota900,
      // blue
      blue: colorPallete.gamma600,
      softblue: colorPallete.gamma200,
      blueShade: colorPallete.delta200,
      verylightBlue: colorPallete.delta300,
      mediumBlue: colorPallete.gamma400,
      lightBlue: colorPallete.delta100,
      vibrantBlue: colorPallete.gamma800,
      darkBlue: colorPallete.delta900,
      // green
      green: colorPallete.epsilon300,
      lightGreen: colorPallete.epsilon100,
      mediumGreen: colorPallete.epsilon500,
      lightDarkGreen: colorPallete.epsilon600,
      darkGreen: colorPallete.epsilon800,
      limeGreen: colorPallete.epsilon700,
      // black
      black: colorPallete.kappa700,
      veryLightBlack: colorPallete.kappa300,
      blackGradient2: colorPallete.kappa400,
      // grey
      veryLightGrey: colorPallete.lambda100,
      mediumLightGrey: colorPallete.lambda200,
      lightGrey: colorPallete.lambda300,
      lowLightGray: colorPallete.lambda500,
      greyShade: colorPallete.lambda800,
      mediumGrey: colorPallete.lambda400,
      vibrantGrey: colorPallete.lambda600,
      darkGrey: colorPallete.lambda900,
      transparentDarkGray: colorPallete.kappa800,
      // orange (fixed swatch - not the dynamic brand color, see `primary`)
      orange: colorPallete.beta700,
      lightOrange: colorPallete.beta200,
      // yellow
      yellow: colorPallete.zeta800,
      lightYellow: colorPallete.zeta100,
      mediumYellow: colorPallete.zeta300,
    },

    // Main buttons
    generalButton: {
      main: primary.main,
      dark: primary.darker,
      contrastText: colorPallete.lota900,
    },
    saveButton: {
      main: colorPallete.epsilon700, // default background color of button
      dark: colorPallete.epsilon900, // background color of button when hover
      contrastText: colorPallete.lota900, // button text color
    },
    cancelButton: {
      main: colorPallete.lambda800, // default background color of button
      dark: colorPallete.lambda900, // background color of button when hover
      contrastText: colorPallete.lota900, // button text color
    },
    secondaryButton: {
      main: colorPallete.delta900, // default background color of button
      dark: colorPallete.gamma200, // background color of button when hover
      contrastText: colorPallete.lota900, // button text color
    },
    closeButton: {
      main: colorPallete.lambda800, // default background color of button
      dark: colorPallete.lambda900, // background color of button when hover
      contrastText: colorPallete.lota900, // button text color
    },
    buttons: {
      pagination: {
        main: primary.main,
        hover: primary.darker,
      },
      inventoryView: {
        main: colorPallete.lambda800,
        hover: colorPallete.lambda700,
        select: colorPallete.lambda900,
      },
      addWidget: colorPallete.lambda600,
      radioButton: primary.main,
    },
    successBadge: {
      main: colorPallete.epsilon700,
      contrastText: colorPallete.lota900,
    },
    warningBadge: {
      main: colorPallete.alpha700,
      contrastText: colorPallete.lota900,
    },
    disabledBadge: {
      main: colorPallete.kappa800,
      contrastText: colorPallete.lambda800,
    },
    stepColors: {
      INPROGRESS: colorPallete.zeta800,
      BGCOLOR1: colorPallete.mu100,
    },
    checkBox: {
      color: primary.main,
      active: primary.main,
    },
    picked: {
      BORDER: colorPallete.gamma100,
      BG: colorPallete.delta100,
    },
    delivered: {
      BORDER: colorPallete.epsilon200,
      BG: colorPallete.epsilon100,
      COLOR: colorPallete.epsilon500,
    },
    widget: {
      baseWidgetTypo: colorPallete.alpha900,
      border: colorPallete.delta200,
      darkBorder: colorPallete.delta300,
    },
    baseResourceColors: {
      SUPERVISOR: brand[400],
      PICKER: colorPallete.gamma300,
    },
    workhourColors: {
      SECOND: colorPallete.lambda800,
      NIGHT: colorPallete.theta900,
      STANDARD: colorPallete.lambda900,
    },
    loader: {
      transparent: colorPallete.loaderBlack,
      black: colorPallete.kappa100,
    },
    icons: {
      searchOutline: colorPallete.lota600,
      doneOutline: primary.main,
      chatIcon: colorPallete.gamma200,
      checkIcon: colorPallete.epsilon300,
      refreshIcon: colorPallete.lambda800,
    },
    textfield: {
      focus: primary.main,
      border: colorPallete.delta200,
    },
    checkBoxColor: { main: primary.main },
    tableBorderColor: colorPallete.lambda400,
    // Don't change or add below
    successColor: colorPallete.epsilon700,
    errorColor: colorPallete.alpha700,
    checkboxActive: primary.main,
    blackOpacity: colorPallete.blackOpacity,
    whiteOpacity: colorPallete.whiteOpacity,
  };
}
